import { useEffect, useRef } from 'react'

import { IMAGE_ASSISTANT_TRIGGER_SELECTOR } from 'Pods/ThreeDStudio/constants'
import { closeImageAssistant } from 'Pods/ThreeDStudio/Store/actions/ui'

// Uploading a file or pasting a URL in the Generate form's image uploader loads that
// image onto the Edit tab's canvas, so while that tab is open the uploader (and its
// sibling paste-URL area) count as part of the panel rather than as outside it.
const EDIT_TAB_INSIDE_SELECTOR = '.image-uploader, .image-uploader__paste-url-area'

// Non-modal side panel: it intentionally leaves the Generate form usable, so it has
// no focus trap. It closes on Escape and on a pointer press outside itself, moves
// focus into itself on open, and restores focus to the trigger on close.
export default function usePanelDismiss(isOpen, panelRef, isEditTab = false) {
  const triggerRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    triggerRef.current = document.activeElement
    panelRef.current?.focus()

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageAssistant()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      // Only pull focus back to the trigger if it is still inside the panel that
      // is closing, so we never yank focus away from elsewhere on the page.
      const panel = panelRef.current
      if (panel?.contains(document.activeElement)) {
        triggerRef.current?.focus?.()
      }
    }
  }, [isOpen, panelRef])

  // Kept apart from the focus effect above so switching to and from the Edit tab
  // only re-binds the listener instead of moving focus back into the panel.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    // The buttons that open the panel sit outside it; counting them as inside lets
    // them switch tabs on an open panel instead of this handler closing it (which
    // resets every tab) just before their onClick reopens it.
    const insideSelector = isEditTab
      ? `${IMAGE_ASSISTANT_TRIGGER_SELECTOR}, ${EDIT_TAB_INSIDE_SELECTOR}`
      : IMAGE_ASSISTANT_TRIGGER_SELECTOR

    // pointerdown rather than click: a drag that starts inside the panel (mask
    // brush, brush-size slider) can end outside it, and the resulting click
    // targets a shared ancestor, which would read as an outside click.
    const handlePointerDown = (event) => {
      const inside = panelRef.current?.contains(event.target)
        || event.target?.closest?.(insideSelector)
      if (!inside) {
        closeImageAssistant()
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)

    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen, isEditTab, panelRef])
}
